import React, { useMemo, useState } from 'react';

// Images (swap these to your newest photos)
import RoomImage1 from '../../assets/images/rooms/the-tavistock-hotel-and-inn-25.webp';
import RoomImage2 from '../../assets/images/rooms/the-tavistock-hotel-and-inn-23.webp';
import RoomImage3 from '../../assets/images/rooms/the-tavistock-hotel-and-inn-19.webp';
import RoomImage4 from '../../assets/images/rooms/the-tavistock-hotel-and-inn-16.webp';
import RoomImage5 from '../../assets/images/rooms/the-tavistock-hotel-and-inn-18.webp';
import RoomImage6 from '../../assets/images/rooms/the-tavistock-hotel-and-inn-1.webp';
import RoomImage7 from '../../assets/images/rooms/the-tavistock-hotel-and-inn-22.webp';

const BOOKING_DOT_COM_URL =
  'https://www.booking.com/hotel/gb/the-tavistock-amp-inn-tavistock.en-gb.html';

const RoomsComponent = () => {
  const roomImagesArray = useMemo(
    () => [
      {
        image: RoomImage1,
        alt: 'Double room with en-suite and soft lighting',
        ariaLabel: 'Room photo 1',
      },
      {
        image: RoomImage2,
        alt: 'Twin or double room with comfortable beds and modern decor',
        ariaLabel: 'Room photo 2',
      },
      {
        image: RoomImage3,
        alt: 'Room with bright window light and fresh bedding',
        ariaLabel: 'Room photo 3',
      },
      {
        image: RoomImage4,
        alt: 'Compact room with desk area and reading lamp',
        ariaLabel: 'Room photo 4',
      },
      {
        image: RoomImage5,
        alt: 'Relaxing room with neutral tones and spacious bed',
        ariaLabel: 'Room photo 5',
      },
      {
        image: RoomImage6,
        alt: 'Bright, airy room with seating area',
        ariaLabel: 'Room photo 6',
      },
      {
        image: RoomImage7,
        alt: 'Warm-toned room with modern furnishings',
        ariaLabel: 'Room photo 7',
      },
    ],
    []
  );

  const room = useMemo(
    () => ({
      name: 'Rooms at The Tavistock Hotel & Inn',
      description:
        'Comfortable rooms with en-suite bathrooms, tea & coffee facilities, and everything you need for a relaxed stay.',
      noteItems: ['Supermarket 1 minute walk away', '24 Hour parking 1 minute walk away'],
    }),
    []
  );

  const [currentIndex, setCurrentIndex] = useState(0);

  const handleNext = () => {
    setCurrentIndex((prev) => (prev === roomImagesArray.length - 1 ? 0 : prev + 1));
  };

  const handlePrev = () => {
    setCurrentIndex((prev) => (prev === 0 ? roomImagesArray.length - 1 : prev - 1));
  };

  const handleSelect = (index) => setCurrentIndex(index);

  return (
    <section id="rooms" className="bg-gray-100 grid h-full w-full">
      <div className="grid gap-y-10 lg:gap-y-12 w-full py-16 px-8 md:px-16 text-center lg:container lg:mx-auto">
        {/* Header */}
        <section className="grid gap-3 mx-auto max-w-3xl">
          <h5 className="text-4xl text-slate-800 font-GaramondBold md:text-5xl font-bold">
            Explore the Rooms
          </h5>
          <p className="text-lg md:text-xl leading-relaxed text-slate-600">
            Updated photos and a simple booking flow — all reservations go through Booking.com.
          </p>
        </section>

        <section className="grid gap-y-8 gap-x-10 lg:grid-cols-2 items-start">
          {/* Details */}
          <section className="grid gap-y-6 text-left bg-white rounded-2xl shadow-sm p-8">
            <div className="grid gap-2">
              <h3 className="text-2xl md:text-3xl font-semibold text-slate-800">{room.name}</h3>
              <p className="text-slate-600 leading-relaxed">{room.description}</p>
            </div>

            <div className="grid gap-3 border border-slate-200 rounded-xl p-6 bg-slate-50">
              <h2 className="text-2xl md:text-3xl font-bold text-slate-800">Book Now</h2>

              <a
                href={BOOKING_DOT_COM_URL}
                target="_blank"
                rel="noreferrer"
                className="inline-grid place-items-center rounded-xl px-6 py-4 text-lg font-semibold bg-[#003b95] text-white hover:bg-slate-800 transition"
                aria-label="Book rooms on Booking.com"
              >
                Book on Booking.com
              </a>

              <p className="text-sm text-slate-600">
                We only accept bookings through Booking.com.
              </p>
            </div>

            <div className="grid gap-2">
              {room.noteItems.map((item) => (
                <p key={item} className="text-slate-700">
                  • {item}
                </p>
              ))}
            </div>
          </section>

          {/* Images */}
          <section className="grid gap-y-4">
            <div className="grid relative w-full mx-auto overflow-hidden rounded-2xl shadow-sm bg-white">
              <div className="grid relative">
                <img
                  src={roomImagesArray[currentIndex].image}
                  alt={roomImagesArray[currentIndex].alt}
                  aria-label={roomImagesArray[currentIndex].ariaLabel}
                  className="w-full object-cover aspect-[4/3]"
                  loading="lazy"
                />

                {/* Controls */}
                <button
                  onClick={handlePrev}
                  className="absolute left-3 top-1/2 -translate-y-1/2 bg-black/50 text-white p-3 rounded-full hover:bg-black/70 transition"
                  aria-label="Previous photo"
                  type="button"
                >
                  &#10094;
                </button>
                <button
                  onClick={handleNext}
                  className="absolute right-3 top-1/2 -translate-y-1/2 bg-black/50 text-white p-3 rounded-full hover:bg-black/70 transition"
                  aria-label="Next photo"
                  type="button"
                >
                  &#10095;
                </button>

                {/* Dots */}
                <div className="absolute bottom-3 left-0 right-0 grid place-items-center">
                  <div className="grid grid-flow-col gap-2 bg-white/80 backdrop-blur rounded-full px-3 py-2">
                    {roomImagesArray.map((_, idx) => (
                      <button
                        key={`dot-${idx}`}
                        type="button"
                        onClick={() => handleSelect(idx)}
                        aria-label={`Go to photo ${idx + 1}`}
                        className={`h-2.5 w-2.5 rounded-full transition ${
                          idx === currentIndex ? 'bg-slate-900' : 'bg-slate-400 hover:bg-slate-600'
                        }`}
                      />
                    ))}
                  </div>
                </div>
              </div>
            </div>

            {/* Thumbnails */}
            <div className="grid grid-flow-col auto-cols-fr gap-3">
              {roomImagesArray.slice(0, 6).map((img, idx) => (
                <button
                  key={`thumb-${idx}`}
                  type="button"
                  onClick={() => handleSelect(idx)}
                  aria-label={`Select photo ${idx + 1}`}
                  className={`grid overflow-hidden rounded-xl border transition ${
                    idx === currentIndex
                      ? 'border-slate-900'
                      : 'border-slate-200 hover:border-slate-400'
                  }`}
                >
                  <img
                    src={img.image}
                    alt={img.alt}
                    className="w-full object-cover aspect-[4/3]"
                    loading="lazy"
                  />
                </button>
              ))}
            </div>
          </section>
        </section>
      </div>
    </section>
  );
};

export default RoomsComponent;
